import { CircularProgress, Divider, InputAdornment, useTheme, Typography, Stack, chipClasses, selectClasses, inputBaseClasses, outlinedInputClasses } from "@mui/material";
import { deepmerge } from "@mui/utils";
import React from "react";
import { capitalizeAndCleanUnderscore } from "@utils";
import AddIcon from "src/icons/AddIcon";
import ChevronDown from "src/icons/ChevronDown";
import SearchBar from "@hyro-components/SearchBar";
import { StyledTextField } from "@hyro-components/fields/text-field/HyroTextField.styles";
import { useSingleSelect } from "./HyroSingleSelect.hook";
import { MenuSx, StyledMenuItem, StyledSearchBar, InputSx, SelectSx } from "./HyroSingleSelect.styles";
import Chip from "@hyro-components/Chip";
import HyroTruncateTypography from "@hyro-components/hyro-truncate-typography/HyroTruncateTypography";
import Tooltip from "@hyro-components/Tooltip";

const MAX_WIDTH = "320px";
const EMPTY_ARRAY = [];
const EMPTY_OBJ = {};

const HyroSingleSelect = ({
  id,
  value,
  showLabel,
  label,
  options = EMPTY_ARRAY,
  onChange,
  onFocus,
  onBlur,
  onPopperClose,
  onMenuClose,
  error,
  isReadOnly,
  onAddNewCollection,
  isFieldReadOnlyFromSpec,
  placeholder,
  loading,
  field,
  testId,
  typography = {
    label: "body1",
    description: "body2"
  },
  open = false,
  helperText,
  SelectProps = EMPTY_OBJ,
  noOptionsMessage,
  capitalizeOption = true,
  fullWidth = true,
  renderValueChip,
  renderOptionChip,
  ...rest
}) => {
  const theme = useTheme();
  const { menuOpen,
    setMenuOpen,
    inputRef,
    filteredOptions,
    optionsExist,
    showSearch,
    handleOpen,
    handleChange,
    setSearchText,
    ADD_NEW,
    showPlaceholder
  } = useSingleSelect({ options, field, noOptionsMessage, onFocus, onPopperClose, onAddNewCollection, onChange, open, value, placeholder, loading });

  return (
    <StyledTextField
      disabled={!!loading}
      readOnly={isReadOnly}
      isFieldReadOnlyFromSpec={isFieldReadOnlyFromSpec}
      isMultiLineSelect={true}
      data-testid={testId}
      slotProps={{
        select: {
          "data-testid": `${testId}-select`,
          renderValue: () => {
            const selectedOption = options?.find(option => option.key === value);
            const displayValue = loading ? "Loading..." : selectedOption?.display_key ?? "";
            const valueText = <HyroTruncateTypography
              variant={typography.label}
              lines={2}>
              {displayValue}
            </HyroTruncateTypography>;
            // Consumers can render their own adornment (e.g. a status chip) next to the value.
            const valueChip = renderValueChip?.(selectedOption);
            if (!valueChip) return valueText;
            return <Stack direction="row" alignItems="center" gap={1} sx={{ overflow: "hidden" }}>
              {valueText}
              {valueChip}
            </Stack>;
          },
          inputRef,
          sx: SelectProps?.sx,
          displayEmpty: true,
          MenuProps: {
            autoFocus: false,
            sx: {
              ...MenuSx,
              ...SelectProps?.MenuProps?.sx
            },
            PaperProps: {
              style: {
                width: "fit-content",
                minWidth: inputRef.current ? inputRef.current.node.clientWidth : "auto",
                maxWidth: MAX_WIDTH,
                maxHeight: onAddNewCollection ? "315px" : "205px",
                ...SelectProps?.MenuProps?.PaperProps?.style
              }
            },
            elevation: 3,
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left"
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left"
            },
            ...onMenuClose ? {
              onClose: () => {
                onMenuClose?.();
              }
            } : {}
          },
          IconComponent: (props) => !loading ? <ChevronDown
            {...props}
            stroke={isReadOnly ? theme.palette.system[400] : theme.palette.text[700]} style={{ top: "unset", right: 12 }}
          /> : null,
          open: menuOpen,
          onClose: () => {
            onBlur?.();
            setSearchText("");
            setMenuOpen(false);
            if (inputRef.current?.node) {
              inputRef.current.node.parentElement.classList.remove("Mui-focused");
            }
          },
          onOpen: () => {
            handleOpen();
            if (inputRef.current?.node) {
              inputRef.current.node.parentElement.classList.add("Mui-focused");
            }
          }
        },
        formHelperText: {
          "data-testid": `${testId}-helper`
        },
        inputLabel: {
          "data-testid": `${testId}-label`
        },
        input: {
          sx: InputSx(loading),
          readOnly: isReadOnly,
          endAdornment: loading ? <InputAdornment sx={{ position: "absolute", right: 12 }} position="end" data-testid={`${testId}-loading`}>
            <CircularProgress color='inherit' size={20} />
          </InputAdornment> : null
        }
      }}
      label={showLabel ? label : ""}
      onChange={handleChange}
      error={!!error}
      helperText={helperText ?? ""}
      select
      fullWidth={fullWidth}
      sx={deepmerge(
        {
          ...SelectSx(showPlaceholder, placeholder || "Select an option"),
          [`& .${inputBaseClasses.root}.${outlinedInputClasses.root}.${inputBaseClasses.formControl}`]: {
            minHeight: "44px"
          }
        },
        SelectProps?.textFieldSx ?? {}
      )}
      id={id}
      value={value ?? ""}
      {...rest}
    >
      {showSearch && <StyledSearchBar>
        <SearchBar stayOpen onChange={setSearchText} customWidth="100%" testId={`${testId}-search`} />
      </StyledSearchBar>}

      {onAddNewCollection &&
        <StyledMenuItem key={ADD_NEW} value={ADD_NEW}>
          <Stack direction={"row"} alignItems={"center"}>
            <div style={{ display: "flex", marginRight: "8px" }}>
              <AddIcon color={theme.palette.text[800]} />
            </div>
            Add New {label}
          </Stack>
        </StyledMenuItem>}

      {filteredOptions?.length > 0 && optionsExist && onAddNewCollection && (
        <Divider sx={{ borderColor: "rgba(45, 44, 68, 0.12)", marginBlock: "4px !important" }} />
      )}

      {(optionsExist || !onAddNewCollection) && filteredOptions?.map((option, optionIndex) => {
        const menuItem = <StyledMenuItem key={`${option.key}-${optionIndex}`} value={option.key} disabled={option.disabled} error={option.error} data-testid={`${testId}-option-${optionIndex}`}>
          <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} width={"100%"}>
            <Stack direction={"column"} alignItems={"flex-start"} gap={0.5} sx={{ overflow: "hidden" }}>
              <Typography variant={typography.label} sx={{ width: "100%", whiteSpace: "wrap" }}>{capitalizeOption ? capitalizeAndCleanUnderscore(option.display_key) : option.display_key}</Typography>
              {option.description && <Typography variant={typography.description} color="text.700" noWrap sx={{ width: "100%" }} className="description" >{option.description}</Typography>}
            </Stack>
            {renderOptionChip
              ? renderOptionChip(option)
              : (option.showNewChip || option.chipText) && (
                <Chip
                  label={option.chipText || "New"}
                  size="small"
                  isRounded={true}
                  textColorPath={["system", "0"]}
                  backgroundColorPath={option.chipText ? ["grey", "500"] : ["secondary", "main"]}
                  sx={{
                    marginLeft: "auto",
                    height: "20px",
                    [`& .${chipClasses.label}`]: {
                      padding: "1px 8px",
                    }
                  }}
                />
              )
            }
          </Stack>
        </StyledMenuItem>;

        if (option.disabled && option.disabledTooltipTitle) {
          return <Tooltip key={`${option.key}-${optionIndex}-disabled-tooltip`} title={option.disabledTooltipTitle} popperStyle={{ marginBottom: "-5px" }}>
            {menuItem}
          </Tooltip>;
        }

        return menuItem;
      })}

    </StyledTextField>
  );
};

export default HyroSingleSelect;
